/**
 * @fileoverview Address formatting utilities for provider search components.
 *
 * Shared utilities for formatting addresses in a compact, consistent way
 * across provider search experiences (case ingestion, main matter, etc.).
 */

/**
 * Remove ", USA" suffix and normalize whitespace.
 * Used for displaying addresses in search results.
 *
 * @example
 * compactLocation("123 Main St, City, CA 90210, USA") // "123 Main St, City, CA 90210"
 */
export const compactLocation = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const normalized = raw.replace(/\s+/g, ' ').trim();
  return normalized.replace(/,\s*USA$/i, '');
};

/**
 * Format address as "line1 • city, state" for compact display.
 * Used for displaying internal provider directory addresses.
 *
 * @example
 * compactInternalLocation("123 Main St, Los Angeles, CA 90210")
 * // "123 Main St • Los Angeles, CA"
 */
export const compactInternalLocation = (
  formattedAddress: string | null | undefined,
): string => {
  const cleaned = compactLocation(formattedAddress);
  if (!cleaned) return '';

  const parts = cleaned
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    const line1 = parts[0];
    const city = parts[1];
    const stateZip = parts[2];
    const state = stateZip.split(' ')[0];
    if (line1 && city && state) {
      return `${line1} • ${city}, ${state}`;
    }
  }

  return cleaned;
};
